import PropTypes from 'prop-types';

import Box, { BoxProps as MuiBoxProps } from '@mui/material/Box';
import { Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  FullLogo,
  SpacesLogo,
  MenuButton,
  ShoppingIcon,
  User,
} from '@classity-edtech/ui-base/dist/components/Displays';
import { globalColours } from '@classity-edtech/ui-base/dist/style/source';
import { Button } from '@classity-edtech/ui-base/dist/components/Inputs/Button/Button';
import useAppMediaQuery from '@classity-edtech/ui-base/dist/hooks/useMediaQuery';
import { useEffect, useState } from 'react';
import { useConfig } from '../../../contexts';
import { AUTH_KEY, clearAuthCookie, getCookieValue } from '@classity-edtech/ui-base';
import { Link, useLocation, useNavigate, redirect } from 'react-router-dom';
import { useGetMyProfileQuery } from '../../../services/users';
import { useLogoutMutation } from '../../../services/authentication';
import { webPaths } from '../../../routes/paths';

export interface HeaderProps extends MuiBoxProps {
  secondaryCTA: string;
  primaryCTA: string;
  aboutUsCTA: string;
  teachCTA: string;
  isExpanded: boolean;
  hideNavigation: boolean;
  brand: string;
}

export const Header = ({
  className,
  secondaryCTA,
  primaryCTA,
  aboutUsCTA,
  teachCTA,
  isExpanded,
  hideNavigation,
  brand,
}: HeaderProps) => {
  const { pathname } = useLocation();
  const { learnUi } = useConfig() as any;
  const learnUiUrl = learnUi?.url;
  const screens = useAppMediaQuery();
  const smallScreens = screens.isMobile || screens.isTablet;
  const navigate = useNavigate();
  const [showDropdown, setShowDropdown] = useState(false);
  const [clientId, setClientId] = useState('');
  const { data: user } = useGetMyProfileQuery();

  const [logout] = useLogoutMutation();

  const toggleShowDropdown = () => {
    setShowDropdown(!showDropdown);
  };

  const navigateHome = () => {
    navigate('/');
  };

  const navigateSpaces = () => {
    navigate(`/${webPaths.spaces}`);
  };

  const handleLogout = async () => {
    const clientId = getCookieValue(AUTH_KEY);

    logout({
      clientId,
    });

    clearAuthCookie();

    navigate('/');
  };

  useEffect(() => {
    setClientId(getCookieValue(AUTH_KEY));
  }, [user]);

  return (
    <Box
      data-testid='header-section'
      sx={{
        transition: 'background 0.3s ease',
        background: 'transparent',
        backdropFilter: 'blur(10px)',
        position: 'sticky',
        top: 0,
        zIndex: 1000,
        borderBottom: isExpanded
          ? {
              xs: 'none',
              md: `solid 1px ${globalColours['$global-colour-border-neutral']}`,
            }
          : {},
      }}
    >
      <Box
        sx={{
          px: { xs: 2, md: 4 },
          maxWidth: isExpanded ? {} : { xs: 'calc(1200px + 16px)', md: 'calc(1200px + 32px)' },
          mx: 'auto',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Box
            sx={{
              py: 2,
              pr: { xs: 0, md: 3 },
              borderRight: isExpanded
                ? {
                    xs: 'none',
                    md: `solid 1px ${globalColours['$global-colour-border-neutral']}`,
                  }
                : {},
            }}
          >
            {(brand === 'classity' && (
              <FullLogo
                sx={{ width: { xs: '100px', md: '140px' }, height: 'auto', cursor: 'pointer' }}
                data-testid='logo-header-nav'
                onClick={navigateHome}
              />
            )) ||
              (brand === 'spaces' && (
                <SpacesLogo
                  sx={{ width: { xs: '175px', md: '245px' }, height: 'auto', cursor: 'pointer' }}
                  data-testid='logo-spaces-header-nav'
                  onClick={navigateSpaces}
                />
              ))}
          </Box>

          {!hideNavigation && (
            <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 2.5, ml: { xs: 0, md: 6 } }}>
              <Link to={webPaths.about} style={{ textDecoration: 'none' }}>
                <Box
                  sx={{
                    textDecoration: 'none',
                    borderRadius: '8px',
                    background:
                      pathname === webPaths.about
                        ? globalColours['$global-colour-background-neutral-active']
                        : '',
                    '&:hover': {
                      background: globalColours['$global-colour-background-neutral-hover'],
                    },
                    width: 'fit-content',
                  }}
                >
                  <Typography
                    variant={smallScreens ? 'body3SemiBold' : 'body2SemiBold'}
                    sx={{
                      color: globalColours['$global-colour-content-secondary'],
                      p: 1,
                    }}
                  >
                    {aboutUsCTA}
                  </Typography>
                </Box>
              </Link>
              <Link to={webPaths.teach} style={{ textDecoration: 'none' }}>
                <Box
                  sx={{
                    textDecoration: 'none',
                    borderRadius: '8px',
                    background:
                      pathname === webPaths.teach
                        ? globalColours['$global-colour-background-neutral-active']
                        : '',
                    '&:hover': {
                      background: globalColours['$global-colour-background-neutral-hover'],
                    },
                    width: 'fit-content',
                  }}
                >
                  <Typography
                    variant={smallScreens ? 'body3SemiBold' : 'body2SemiBold'}
                    sx={{
                      color: globalColours['$global-colour-content-secondary'],
                      p: 1,
                    }}
                  >
                    {teachCTA}
                  </Typography>
                </Box>
              </Link>
            </Box>
          )}

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              py: 2,
              pl: { xs: 0, md: 3 },
              ml: 'auto',
              borderLeft: isExpanded
                ? {
                    xs: 'none',
                    md: `solid 1px ${globalColours['$global-colour-border-neutral']}`,
                  }
                : {},
            }}
          >
            {!user || !clientId ? (
              <>
                <Link to={learnUiUrl} target='_blank' style={{ textDecoration: 'none' }}>
                  <Box
                    sx={{
                      textDecoration: 'none',
                      borderRadius: '8px',
                      '&:hover': {
                        background: globalColours['$global-colour-background-neutral-hover'],
                      },
                      width: 'fit-content',
                    }}
                  >
                    <Typography
                      variant={smallScreens ? 'body3' : 'body2'}
                      sx={{
                        color: globalColours['$global-colour-interactive-primary-default'],
                        p: 1,
                      }}
                    >
                      {secondaryCTA}
                    </Typography>
                  </Box>
                </Link>
                <Link to={learnUiUrl} target='_blank' style={{ textDecoration: 'none' }}>
                  <Box sx={{ textDecoration: 'none' }}>
                    <Button
                      variant='outlined'
                      sx={{
                        px: { xs: 1.5, md: 3 },
                        py: { xs: '5px', md: 1.5 },
                        '&:hover': {
                          background: globalColours['$global-colour-interactive-accent-default'],
                          color: globalColours['$global-colour-base-contrast'],
                        },
                        width: 'fit-content',
                      }}
                    >
                      <Typography variant={smallScreens ? 'body3SemiBold' : 'body2SemiBold'}>
                        {primaryCTA}
                      </Typography>
                    </Button>
                  </Box>
                </Link>
              </>
            ) : (
              <>
                {/* <MenuButton
                  {...({
                    icon: (
                      <ShoppingIcon
                        sx={{
                          width: '16px',
                          height: '16px',
                          color: globalColours['$global-colour-interactive-primary-default'],
                        }}
                      />
                    ),
                    size: 'small',
                    hideSideIcon: true,
                  } as any)}
                /> */}
                <Box sx={{ position: 'relative' }}>
                  <MenuButton
                    {...({
                      icon: (
                        <User
                          sx={{
                            width: '16px',
                            height: '16px',
                            color: globalColours['$global-colour-interactive-primary-default'],
                          }}
                        />
                      ),
                      size: 'small',
                      hideAlert: true,
                      onClick: toggleShowDropdown,
                      sx: {
                        pr: { xs: 0, md: 1 },
                      },
                    } as any)}
                  />

                  {showDropdown && (
                    <>
                      <Box
                        sx={{
                          position: 'absolute',
                          left: 0,
                          top: '100%',
                          background: globalColours['$global-colour-background-elevated'],
                          p: 1,
                          minWidth: '120px',
                          cursor: 'pointer',
                        }}
                        onClick={handleLogout}
                      >
                        <Typography variant='body2SemiBold'>Logout</Typography>
                      </Box>
                    </>
                  )}
                </Box>
              </>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

Header.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  hideNavigation: PropTypes.bool,
  brand: PropTypes.string,
};

Header.defaultProps = {
  children: null,
  className: null,
  hideNavigation: false,
  brand: 'classity',
};

export default styled<any>(Header)(({ theme }) => ``);
